import { Component, EventEmitter, NgZone, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { EmailEditorComponent } from 'angular-email-editor';
import { ConfirmationService } from 'primeng/api';
import { forkJoin, Observable, ReplaySubject, Subject } from 'rxjs';
import { IPendingChangesComponent } from 'src/app/guards/pending-changes.guard';
import { Campaign, DripCampaignEditContext, EmailType } from 'src/app/models/communication/campaign';
import { EditorType, EXPERT_MODE_HELP_HTML, unlayerDesignHasContent } from 'src/app/models/communication/email-html-import';
import { Template } from 'src/app/models/communication/template';
import { Employer } from 'src/app/models/employer';
import { DataService } from 'src/app/services/data.service';
import { SiteService } from 'src/app/services/site.service';
import { ExpertHtmlEditorComponent } from '../expert-html-editor/expert-html-editor.component';

type CampaignTab = 'details' | 'email' | 'sms';

@Component({
  selector: 'app-campaign-details',
  templateUrl: './campaign-details.component.html',
  styleUrls: ['./campaign-details.component.scss']
})
export class CampaignDetailsComponent implements IPendingChangesComponent {

	@ViewChild(EmailEditorComponent) emailEditor: EmailEditorComponent;
	@ViewChild(ExpertHtmlEditorComponent) expertEditor: ExpertHtmlEditorComponent;

	// Designer (Unlayer) vs Expert (HTML). modeSelection is what the toggle shows; it is
	// snapped back to campaign.editorTypeID when the user cancels a switch.
	public editorType = EditorType;
	public expertHelp = EXPERT_MODE_HELP_HTML;
	public modeSelection: number = EditorType.Designer;
	// The Designer content as it was when the user switched to Expert, kept in memory so
	// switching back restores it. (Designer → Expert converts the design to HTML; HTML edits
	// can't be turned back into a design.)
	private designBeforeHtml: string = null;
	public modeOptions = [
		{ label: 'Designer', value: EditorType.Designer, icon: 'pi pi-th-large' },
		{ label: 'Expert (HTML)', value: EditorType.Html, icon: 'pi pi-code' }
	];

	// Details = the form fields; Email = the editor, given the whole viewport; SMS = the text message.
	public activeTab: CampaignTab = 'details';
	public tabs: Array<{ id: CampaignTab, label: string }> = [
		{ id: 'details', label: 'Details' },
		{ id: 'email', label: 'Email' },
		{ id: 'sms', label: 'SMS' }
	];

	public campaign: Campaign;
	public employerType: 'all' | 'selected';
	public allEmployers: Array<{ employerID: string, employerName: string, selected: boolean }> = []; //all employers
	public filteredEmployers: Array<{ employerID: string, employerName: string, selected: boolean }> = []; //filtered list to show in the dialog
	public selectedEmployers: Array<{ employerID: string, employerName: string, selected: boolean }> = []; //actual selected employers (keeping this separate from allemployers so you can cancel the dialog and not lose your selection)
	public employeeSearchTerm: string = "";
	public showEmployeeChooser: boolean = false;

	public emailType = EmailType;

	public templates: Array<Template> = [];
	public selectedTemplate: string;
	public editorOptions = {};
	private isNewCampaign: boolean = false;
	private gotCampaign: EventEmitter<boolean> = new EventEmitter();
	private gotEmployers: EventEmitter<boolean> = new EventEmitter();

	public testFormVisible: boolean = false;
	public testEmail: string = "";
	public testPhone: string = "";
	public testEmployer: Employer;

	public errors: any = {};
	public isDirty: boolean = false;
	public missingEmployers: any[] = [];
	public showMissingWarning: boolean = false;
	public statusId: number;

	// Set when opened from the drip program screen (?dripProgramID=). Decides whether Save
	// updates this campaign in place or creates a private copy of it for that program.
	public dripProgramID: string = null;
	public dripContext: DripCampaignEditContext = null;
	public cloneName: string = '';
	private cloning: boolean = false;

	/** Save will create a private copy: opened from a program and the email is used by other programs too. */
	get willClone(): boolean {
		return !!this.dripContext && this.dripContext.otherProgramCount > 0;
	}
	/** The copy can't be made: this step already went out to an employer on this program. */
	get cloneBlocked(): boolean {
		return this.willClone && this.dripContext.sentToProgramEmployers;
	}

	constructor(private dataService: DataService, private route: ActivatedRoute, private router: Router, private siteService: SiteService, private confirmationService: ConfirmationService, private zone: NgZone) { }

	ngOnInit() {
		forkJoin([this.gotCampaign.asObservable(), this.gotEmployers.asObservable()]).subscribe(() => {
			// For drip campaigns, force all employers
			if (this.campaign.emailTypeID === EmailType.Drip) {
				this.employerType = 'all';
				this.selectedEmployers = [];
			} else {
				this.selectedEmployers = this.allEmployers.filter((employer) => {
					return (this.campaign.employers ?? []).includes(employer.employerID); // a new campaign has no employers yet
				});
				this.selectedEmployers.forEach((employer) => {
					this.allEmployers.find((e) => e.employerID === employer.employerID).selected = true;
				});
			}
		});
		this.route.paramMap.subscribe(p => {
			let id = p.get('id');
			if(id == 'new') {
				this.siteService.pageTitle = 'New Campaign';
				this.campaign = new Campaign({ campaignID: crypto.randomUUID(), campaignStatusID: 4 });
				this.modeSelection = this.campaign.editorTypeID;
				this.isNewCampaign = true;
				this.gotCampaign.emit(true);
				this.gotCampaign.complete();
				this.statusId = 4;
				this.updateHeaderButtons();
			} else {
				this.dripProgramID = this.route.snapshot.queryParamMap.get('dripProgramID');
				this.refreshCampaign(id).then(() => {
					this.siteService.pageTitle = this.campaign.campaignName;
					this.gotCampaign.emit(true);
					this.gotCampaign.complete();
					this.statusId = this.campaign.campaignStatusID;
					this.loadDripContext();
				});
			}
			this.setBackButton();
		});
		this.dataService.getEmployers().subscribe((data) => {
			this.allEmployers = data.map((employer) => {
				return { employerID: employer.employerID, employerName: employer.employerName, selected: false };
			});
			this.filteredEmployers = this.allEmployers;
			this.gotEmployers.emit(true);
			this.gotEmployers.complete();
		});
		this.dataService.getTemplates().subscribe((data) => {
			this.templates = data;
		});
		this.updateHeaderButtons();
		this.setBackButton();

	}

	/** Back goes to the drip program this step was opened from, otherwise to the campaign list. */
	private setBackButton() {
		this.siteService.backButton = this.dripProgramID
			? { title: 'Back to Drip Program', link: '/admin/campaigns/drip-programs', queryParams: { programID: this.dripProgramID } }
			: { title: 'Back to Campaigns', link: '/admin/campaigns' };
	}

	/** Opened from a drip program: find out whether Save should copy this campaign for that program. */
	private loadDripContext() {
		this.dripContext = null;
		if (!this.dripProgramID) return;
		this.dataService.getDripCampaignEditContext(this.dripProgramID, this.campaign.campaignID).subscribe({
			next: (ctx: DripCampaignEditContext) => {
				this.dripContext = ctx;
				this.cloneName = `${this.campaign.campaignName} - ${ctx.dripProgramDesc}`;
				this.updateHeaderButtons();
			},
			error: () => {
				// Fail closed: without the context we can't tell whether Save should copy this email for
				// the program or change it everywhere, so save() refuses while dripProgramID is set.
				this.siteService.setMessage(CampaignDetailsComponent.NO_DRIP_CONTEXT, 'error', 8000);
			}
		});
	}

	refreshCampaign(id: string) {
		return new Promise<void>((resolve) => {
			this.dataService.getCampaign(id).subscribe((data:any) => {
				let campaign = new Campaign(data);
				this.campaign = campaign;
				this.modeSelection = this.campaign.editorTypeID;
				this.employerType = this.campaign.sendAllEmployers ? 'all' : 'selected';
				this.updateHeaderButtons();
				resolve();
			});
		});
	}
	selectEmployers() {
		// Don't allow employer selection for drip campaigns
		if (this.campaign && this.campaign.emailTypeID === EmailType.Drip) {
			return;
		}
		
		this.filteredEmployers = this.allEmployers;
		this.showEmployeeChooser = true;
	}

	filterEmployers() {
		this.filteredEmployers = this.allEmployers.filter((employer) => {
			return employer.employerName.toLowerCase().includes(this.employeeSearchTerm.toLowerCase());
		});
	}

	toggleActive() {
		if(this.statusId == 4) {
			this.statusId = this.campaign.campaignStatusID == 4 ? 1 : this.campaign.campaignStatusID;
		} else {
			this.statusId = 4;
		}
		this.isDirty = true;
	}
	setCampaignType(type: EmailType) {
		// Email type is set via the radio button binding
		// Update header buttons based on campaign type
		this.updateHeaderButtons();
		
		// For drip campaigns, force all employers
		if (type === EmailType.Drip) {
			this.employerType = 'all';
			this.selectedEmployers = [];
		}
	}
	confirm() {
		this.selectedEmployers = this.allEmployers.filter((employer) => {
			return employer.selected;
		});
		this.showEmployeeChooser = false;
		this.employerType = this.selectedEmployers.length > 0 ? 'selected' : 'all';
	}
	close() {
		this.showEmployeeChooser = false;
		this.employeeSearchTerm = "";
	}

	get isHtml(): boolean {
		return this.campaign?.editorTypeID === EditorType.Html;
	}

	selectTab(tab: CampaignTab) {
		this.activeTab = tab;
		// The Email workspace is sized to the viewport; start it at the top of the page.
		if (tab === 'email') window.scrollTo(0, 0);
	}

	// Which tab shows each validation key; anything not listed is on Details.
	private static readonly ERROR_TAB: Record<string, CampaignTab> = {
		eMailSubject: 'email',
		eMailContent: 'email',
		emailJSON: 'email',
		smsMessage: 'sms'
	};

	tabHasErrors(tab: CampaignTab): boolean {
		return Object.keys(this.errors ?? {}).some(k => !!this.errors[k] && (CampaignDetailsComponent.ERROR_TAB[k] ?? 'details') === tab);
	}

	/** After a failed save, if the current tab has no errors, switch to the first tab that does. */
	private focusTabWithErrors() {
		if (this.tabHasErrors(this.activeTab)) return;
		const withErrors = this.tabs.find(t => this.tabHasErrors(t.id));
		if (withErrors) this.selectTab(withErrors.id);
	}

	/** Reads the live Unlayer editor (unsaved work included) and returns to Angular's zone. */
	private readDesigner(cb: (design: any, html: string) => void) {
		this.emailEditor.exportHtml((data: any) => this.zone.run(() => cb(data.design, data.html.replace(/&amp;/ig, '&'))));
	}

	/** Toggle handler. Switching is confirmed when it would discard or convert content. */
	requestModeChange(target: number) {
		if (target === this.campaign.editorTypeID) return;
		const revert = () => this.modeSelection = this.campaign.editorTypeID;

		if (target === EditorType.Html) {
			// Check the live editor: a design that hasn't been saved yet isn't in campaign.emailJSON.
			this.readDesigner((design, html) => {
				if (!unlayerDesignHasContent(design)) { this.switchToHtml('', null); return; }
				this.confirmationService.confirm({
					message: "Switch to Expert (HTML) mode? Your design will be converted to HTML as a starting point. If you switch back to the Designer, your design will be restored, but changes made in HTML won't carry over.",
					header: "Switch to Expert mode?",
					acceptLabel: "Switch",
					rejectLabel: "Cancel",
					acceptIcon: 'check',
					rejectIcon: 'times',
					accept: () => { this.switchToHtml(html, JSON.stringify(design)); this.importConvertedImages(); },
					reject: revert
				});
			});
		} else {
			const restoring = !!this.designBeforeHtml;
			if (!restoring && !this.campaign.eMailContent?.trim()) { this.switchToDesigner(); return; }
			this.confirmationService.confirm({
				message: restoring
					? "Switch back to the Designer? Your design from before you switched will be restored. Changes made in HTML will be discarded."
					: "Switch back to the Designer? The HTML in this campaign will be discarded.",
				header: "Discard HTML?",
				acceptLabel: "Discard and switch",
				acceptButtonStyleClass: 'danger',
				rejectLabel: "Cancel",
				acceptIcon: 'check',
				rejectIcon: 'times',
				accept: () => this.switchToDesigner(),
				reject: revert
			});
		}
	}
	/** After Designer → Expert, copy the design's images (hosted by Unlayer) to One Morning storage, as a paste would. */
	private importConvertedImages() {
		// The expert editor is created by *ngIf on the next change-detection pass.
		setTimeout(() => this.expertEditor?.reimportImages());
	}
	/** @param designBefore the design being converted, kept for switching back; undefined keeps the current copy. */
	private switchToHtml(html: string, designBefore?: string) {
		if (designBefore !== undefined) this.designBeforeHtml = designBefore;
		this.campaign.eMailContent = html;
		this.campaign.emailJSON = null;
		this.campaign.editorTypeID = EditorType.Html;
		this.modeSelection = EditorType.Html;
		this.isDirty = true;
	}
	/** @param designJson the design to open; defaults to the one kept from before switching to Expert. */
	private switchToDesigner(designJson: string = this.designBeforeHtml ?? this.campaign.emailJSON) {
		this.campaign.eMailContent = '';
		this.campaign.emailJSON = designJson;
		this.designBeforeHtml = null;
		this.campaign.editorTypeID = EditorType.Designer;
		this.modeSelection = EditorType.Designer;
		this.isDirty = true;
		// The <email-editor> is re-created by *ngIf and editorLoaded() loads emailJSON (if any).
	}

	editorLoaded(e: any) {
		if(this.campaign.emailJSON && this.campaign.emailJSON !== '{}') {
			setTimeout(() =>  this.emailEditor?.loadDesign(JSON.parse(this.campaign.emailJSON)), 1000);
		}/*
		(this.emailEditor.editor as any).registerTool({
			name: 'substitution_fields',
			label: 'Substitution Fields',
			icon: 'fa fa-tags',
			supportedDisplayModes: ['web', 'email'],
			options: {
				field: {
					title: 'Substitution Field',
					defaultValue: ''
				}
			},
			renderer: {
				Viewer: (values: any) => {
					return { html: `<span class="substitution-field">${values.field}</span>` };
				},
				exporters: {
					web: (values: any) => {
						return `<span class="substitution-field">${values.field}</span>`;
					},
					email: (values: any) => {
						return `<span class="substitution-field">${values.field}</span>`;
					}
				}
			}
		})
			*/

		this.emailEditor?.editor.registerCallback('design:updated', () => {
			this.isDirty = true;
		});
	}

	markDirty(e:any) {
		this.isDirty = true;
	}
	templateSelected(e: any) {
		const template: Template = e.value;
		if (!template) return;
		const sourceIsHtml = template.editorTypeID === EditorType.Html;
		const sourceEmpty = sourceIsHtml ? !template.templateHtml?.trim() : (!template.templateJSON || template.templateJSON == '{}');
		if (sourceEmpty) return;

		// Same copy-no-link behavior as before: the template's content is copied into the
		// campaign; the campaign keeps no reference back to the template.
		const apply = () => {
			if (sourceIsHtml) {
				// From the Designer the current design is being replaced on purpose, so nothing is kept.
				this.switchToHtml(template.templateHtml, this.isHtml ? undefined : null);
			} else if (this.isHtml) {
				this.switchToDesigner(template.templateJSON);
			} else {
				this.emailEditor.loadDesign(JSON.parse(template.templateJSON));
				this.isDirty = true;
			}
		};

		const confirmReplace = () => this.confirmationService.confirm({
			message: "This template is not empty.  By selecting a new template, you will lose any changes you have made to this template.",
			header: "Are you sure?",
			acceptLabel: "Select Template",
			rejectLabel: "Cancel",
			acceptIcon: 'check',
			rejectIcon: 'times',
			accept: apply,
			reject: () => {
				this.selectedTemplate = null;
			}
		});

		if (this.isHtml) {
			if (this.campaign.eMailContent?.trim()) confirmReplace(); else apply();
		} else {
			// Check the live editor so unsaved design work counts as content.
			this.readDesigner(design => unlayerDesignHasContent(design) ? confirmReplace() : apply());
		}
	}

	copyToClipboard(field: string) {
		navigator.clipboard.writeText(field).then(() => {
			this.siteService.setMessage('Copied to clipboard');
		})
	}
	canDeactivate(): boolean | Observable<boolean> {
		return !this.isDirty;
	}
	private static readonly NO_DRIP_CONTEXT = "Couldn't confirm whether this email is shared with other drip programs, so it can't be saved here. Go back to the drip program and open it again.";

	async save() {
		return new Promise<boolean>(async (resolve, reject) => {
			this.errors = {};
			// Opened from a drip program: never save until we know whether this email is shared, or an
			// edit meant for one program could land on every program that uses it.
			if (this.dripProgramID && !this.dripContext) {
				this.siteService.setMessage(CampaignDetailsComponent.NO_DRIP_CONTEXT, 'error', 8000);
				reject(false); // reject, not resolve: PendingChangesGuard treats resolve as saved and leaves the page
				return;
			}
			if (this.isHtml) {
				if (this.expertEditor?.errorCount > 0) {
					this.errors.eMailContent = 'Fix the errors listed in the HTML issues panel before saving.';
					this.focusTabWithErrors();
					return;
				}
				this.campaign.emailJSON = null;
			} else {
				await new Promise<void>((resolve2) => {
					this.emailEditor.exportHtml((data: any) => {
						let html = data.html.replace(/&amp;/ig, '&');
						this.campaign.eMailContent = html;
						this.campaign.emailJSON = JSON.stringify(data.design);
						resolve2();
					});
				});
			}
			this.campaign.sendAllEmployers = this.employerType === 'all';
			this.campaign.employers = this.selectedEmployers.map(e => e.employerID);
			this.campaign.campaignStatusID = this.statusId;
			
			// For drip campaigns, force sendAllEmployers to true
			if (this.campaign.emailTypeID === EmailType.Drip) {
				this.campaign.sendAllEmployers = true;
				this.campaign.employers = [];
			}

			// Shared email edited from inside a copied program: save it as a private copy for that
			// program (new ID, user-supplied name) and repoint the program's step at the copy.
			if (this.willClone) {
				if (this.cloneBlocked || this.cloning) { resolve(false); return; }
				const name = this.cloneName?.trim();
				if (!name) {
					this.errors.cloneName = 'Enter a name for the private copy.';
					resolve(false);
					return;
				}
				const clone = new Campaign({ ...this.campaign, campaignID: crypto.randomUUID(), campaignName: name });
				this.cloning = true;
				this.dataService.cloneDripCampaign(this.dripProgramID, this.campaign.campaignID, clone).subscribe({
					next: (res: any) => {
						this.cloning = false;
						this.isDirty = false;
						this.missingEmployers = res?.missingEmployers ?? [];
						const programDesc = this.dripContext.dripProgramDesc;
						// Open the copy in the same program context. It is no longer shared, so later saves are in place.
						this.router.navigate(['/admin', 'campaigns', clone.campaignID], { queryParams: { dripProgramID: this.dripProgramID } }).then(() => {
							this.siteService.setMessage(`Private copy created for ${programDesc}`);
							if (this.missingEmployers.length) this.showMissingWarning = true;
						});
						resolve(true);
					},
					error: (err) => {
						this.cloning = false;
						if (err?.status === 400) {
							// The server's guard text (already sent, not shared, ...) or field validation errors.
							const msg = err.error?.message ?? (typeof err.error === 'string' ? err.error : null);
							if (msg) this.siteService.setMessage(msg, 'error', 8000);
							this.errors = err.error?.errors ?? {};
							this.focusTabWithErrors();
						}
						reject(false);
					}
				});
				return;
			}

			let campaignToSave = new Campaign(this.campaign); //this is to prevent the deliveryDateTime from flashing on the page before save.
			this.dataService.saveCampaign(campaignToSave).subscribe((res: any) => {
				this.siteService.setMessage('Campaign saved');
				this.isNewCampaign = false;
				this.refreshCampaign(this.campaign.campaignID);
				this.isDirty = false;
				this.missingEmployers = res?.missingEmployers ?? [];
				if (this.missingEmployers.length) this.showMissingWarning = true;
				resolve(true);
			}, (err) => {
				this.errors = err.error?.errors ?? {};
				this.focusTabWithErrors();
				reject(false);
			});
		});

	}

	delete() {
		this.confirmationService.confirm({
			message: "Are you sure you want to delete this campaign?  This action cannot be undone.",
			header: "Delete Campaign?",
			acceptLabel: "Delete Campaign",
			acceptButtonStyleClass: 'danger',
			rejectLabel: "Cancel",
			acceptIcon: 'check',
			rejectIcon: 'times',
			accept: () => {
				this.dataService.deleteCampaign(this.campaign.campaignID).subscribe({
					next: () => {
						this.isDirty = false;
						this.router.navigate(['/admin', 'campaigns']).then(() => {
							this.siteService.setMessage('Campaign deleted');
						});
					},
					error: (err) => {
						// 400 = refused by the server (in use by a drip program); 500s already get the interceptor's toast.
						if (err?.status === 400) this.siteService.setMessage(err.error?.message ?? 'This campaign could not be deleted.', 'error', 6000);
					}
				});
			}
		})
	}


	test() {
		this.testFormVisible = true;
		this.testEmail = "";
		this.testPhone = "";
		this.testEmployer = null;
	}

	sendTest() {
		if (this.testEmail && this.testEmployer) {
			// The server sends the saved campaign, so unsaved edits (a subject just typed in,
			// say) wouldn't be in the test.
			if (this.isDirty) {
				this.siteService.setMessage('Save your changes first. The test sends the saved version of the campaign.', 'warn', 5000);
				return;
			}
			this.dataService.testCampaign(this.campaign.campaignID, this.testEmail, this.testPhone, this.testEmployer.employerID).subscribe({
				next: () => {
					this.siteService.setMessage('Test communications sent');
					this.testFormVisible = false;
				},
				// 400s carry a plain explanation from the server; 500s are already reported by the error interceptor.
				error: (err) => {
					if (err?.status === 400) {
						const msg = typeof err.error === 'string' ? err.error : (err.error?.title ?? 'The test could not be sent.');
						this.siteService.setMessage(msg, 'error', 6000);
					}
				}
			});
		}
	}

	execute() {
		this.confirmationService.confirm({
			message: "Are you sure you want to execute this campaign?  The campaign will be delivered immediately.",
			header: "Execute Campaign?",
			acceptLabel: "Execute Campaign",
			rejectLabel: "Cancel",
			acceptIcon: 'check',
			rejectIcon: 'times',
			accept: () => {
				this.dataService.executeCampaign(this.campaign.campaignID).subscribe(() => {
					this.siteService.setMessage('Campaign executing');
				});
			}
		})
	}

	get selectedEmployerNames() {
		let names = this.selectedEmployers.slice(0,3).map(e => e.employerName).join(", ");
		if (this.selectedEmployers.length > 3) {
			names += ` + ${this.selectedEmployers.length - 3} more`;
		}
		return names;
	}

	updateHeaderButtons() {
		const isDripCampaign = this.campaign && this.campaign.emailTypeID === EmailType.Drip;
		
		// Build actions menu - exclude Execute for drip campaigns
		const actions = [
			{ text: 'Test', callback: () => this.test() }
		];
		
		if (!isDripCampaign) {
			actions.push({ text: 'Execute', callback: () => this.execute() });
		}

		this.siteService.headerButtons = [
			{ text: 'Actions', class: 'neutral', children: actions },
			// Blocked: a plain disabled Save, not a "Save as Copy" that can't be used.
			{ text: this.willClone && !this.cloneBlocked ? 'Save as Copy' : 'Save', class: 'success', callback: () => this.save(), disabled: this.cloneBlocked },
			{ text: 'Delete', class: 'danger', callback: () => this.delete() }
		];
	}
}
